<!--
  Hermes → Community → Recruiting config panel.
  Skeleton only. Save is a mock toast until core-api ships the endpoint.
-->

<template>
  <div class="box mt-10">
    <md-card>
      <md-card-expand>
        <md-card-actions md-alignment="space-between">
          <md-card-expand-trigger>
            <md-button class="community-expand-button m-left-5">
              <div>Recruiting</div>
            </md-button>
          </md-card-expand-trigger>
        </md-card-actions>

        <md-card-expand-content>
          <md-card-content class="rules card-content">
            <div>
              <form
                @submit.prevent="save('form-recruiting')"
                @change="saveText = 'Save'"
                autocomplete="off"
                data-vv-scope="form-recruiting"
                class="form-recruiting"
              >
                <!-- Module activation -->
                <section class="recruiting-section">
                  <h4 class="recruiting-section__title">Module Activation</h4>

                  <div class="recruiting-check-row">
                    <md-checkbox v-model="config.moduleEnabled" class="approval-request">
                      Recruiting Module
                    </md-checkbox>
                    <p class="recruiting-helper">
                      When unchecked, Opportunities are not shown in the Dashboard.
                    </p>
                  </div>

                  <div v-if="config.moduleEnabled" class="recruiting-field-row">
                    <label class="recruiting-field-row__label">
                      Job Posting URL (auto-generated):
                    </label>
                    <md-field class="create-rule">
                      <md-input
                        :value="wizardURLDisplay"
                        readonly
                        name="jobPostingUrl"
                        placeholder="URL will be generated when module is enabled"
                      ></md-input>
                    </md-field>
                    <p class="recruiting-helper">
                      Integrated into the Program Page like Channels URLs.
                    </p>
                  </div>

                  <div v-if="config.moduleEnabled" class="recruiting-field-row recruiting-learn-more">
                    <label class="recruiting-field-row__label">
                      Recruiting Program Page:
                    </label>
                    <md-field class="create-rule">
                      <md-input
                        v-model.lazy="config.learnMoreURL"
                        name="learnMoreURL"
                        placeholder="https://example.com/careers/learn-more"
                        @input="saveText = 'Save'"
                      ></md-input>
                    </md-field>
                    <p class="recruiting-helper">
                      Destination for the "Learn More" link at the bottom of
                      the recruiting wizard.
                    </p>
                  </div>
                </section>

                <!-- Administrators Alerts-->
                <template v-if="config.moduleEnabled">
                  <section class="recruiting-section">
                    <h4 class="recruiting-section__title">Administrators</h4>
                    <p class="recruiting-section__desc">
                      Assign individual members from the community for each
                      role. Selection pattern mirrors Requests (existing
                      member pool).
                    </p>

                    <div
                      v-for="role in adminRoles"
                      :key="role.key"
                      class="recruiting-field-row"
                    >
                      <label class="recruiting-field-row__label">
                        {{ role.label }}
                      </label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.trim="config.administrators[role.key]"
                          :name="`admin_${role.key}`"
                          :placeholder="role.placeholder"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">{{ role.helper }}</p>
                    </div>
                  </section>

                  <!-- §5.1.3 — Notifications -->
                  <section class="recruiting-section">
                    <h4 class="recruiting-section__title">Notifications</h4>

                    <div class="recruiting-check-row">
                      <md-checkbox
                        v-model="config.notifications.requireApprovals"
                        class="approval-request"
                      >
                        Require approvals for jobs?
                      </md-checkbox>
                      <p class="recruiting-helper">
                        When checked, admin specifies recipient emails
                        directly (not selected from the member pool).
                      </p>
                    </div>

                    <div
                      v-if="config.notifications.requireApprovals"
                      class="recruiting-field-row"
                    >
                      <label class="recruiting-field-row__label">
                        Approval recipient emails:
                      </label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.trim="config.notifications.approvalEmails"
                          name="approval_emails"
                          placeholder="email1@example.com, email2@example.com"
                          md-autogrow
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">
                        Comma-separated. Receives notification when a new
                        job needs review.
                      </p>
                    </div>

                    <div class="recruiting-check-row">
                      <md-checkbox
                        v-model="config.notifications.jobReminder"
                        class="approval-request"
                      >
                        Job Reminder?
                      </md-checkbox>
                      <p class="recruiting-helper">
                        Sends a reminder to alums who haven't engaged
                        with the opportunity after a configurable window.
                      </p>
                    </div>
                  </section>

                  <!-- §5.1.4 — Matching Rules -->
                  <section class="recruiting-section">
                    <h4 class="recruiting-section__title">Matching Rules</h4>
                    <p class="recruiting-section__desc">
                      Mirrors the existing Advice rules block.
                    </p>

                    <div class="recruiting-field-row">
                      <label class="recruiting-field-row__label">
                        Minimum advisors that can be matched to a job:
                      </label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.number="config.matching.minAdvisorsPerJob"
                          name="min_advisors_per_job"
                          type="number"
                          v-validate="'required|min_value:1'"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">Default: 25</p>
                      <p
                        class="recruiting-error"
                        v-show="errors.has('form-recruiting.min_advisors_per_job')"
                      >
                        {{ errors.first('form-recruiting.min_advisors_per_job') }}
                      </p>
                    </div>

                    <div class="recruiting-check-row">
                      <md-checkbox
                        v-model="config.matching.refillEnabled"
                        class="approval-request"
                      >
                        Refill
                      </md-checkbox>
                      <p class="recruiting-helper">
                        Top-up advisors when interest thresholds aren't met.
                      </p>
                    </div>

                    <div class="recruiting-field-row">
                      <label class="recruiting-field-row__label">Interest Clicks:</label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.number="config.matching.interestClicks"
                          name="interest_clicks"
                          type="number"
                          v-validate="'required|min_value:0'"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">
                        Default: 5 — threshold of click engagement before
                        triggering refill.
                      </p>
                      <p
                        class="recruiting-error"
                        v-show="errors.has('form-recruiting.interest_clicks')"
                      >
                        {{ errors.first('form-recruiting.interest_clicks') }}
                      </p>
                    </div>

                    <div class="recruiting-field-row">
                      <label class="recruiting-field-row__label">Days to Refill:</label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.number="config.matching.daysToRefill"
                          name="days_to_refill"
                          type="number"
                          v-validate="'required|min_value:1'"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">Default: 3</p>
                      <p
                        class="recruiting-error"
                        v-show="errors.has('form-recruiting.days_to_refill')"
                      >
                        {{ errors.first('form-recruiting.days_to_refill') }}
                      </p>
                    </div>

                    <div class="recruiting-field-row">
                      <label class="recruiting-field-row__label">
                        Minimum advisors for refill:
                      </label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.number="config.matching.minAdvisorsForRefill"
                          name="min_advisors_for_refill"
                          type="number"
                          v-validate="'required|min_value:1'"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">Default: 5</p>
                      <p
                        class="recruiting-error"
                        v-show="errors.has('form-recruiting.min_advisors_for_refill')"
                      >
                        {{ errors.first('form-recruiting.min_advisors_for_refill') }}
                      </p>
                    </div>

                    <div class="recruiting-field-row">
                      <label class="recruiting-field-row__label">Maximum advisors per job:</label>
                      <md-field class="create-rule">
                        <md-input
                          v-model.number="config.matching.maxAdvisorsPerJob"
                          name="max_advisors_per_job"
                          type="number"
                          v-validate="'required|min_value:1'"
                        ></md-input>
                      </md-field>
                      <p class="recruiting-helper">Default: 100</p>
                      <p
                        class="recruiting-error"
                        v-show="errors.has('form-recruiting.max_advisors_per_job')"
                      >
                        {{ errors.first('form-recruiting.max_advisors_per_job') }}
                      </p>
                    </div>
                  </section>

                  <!-- Patterns - Skeleton only — the actual Library / Editor /
                       Test / Quarantine views are their own routes
                       (separate tickets). -->
                  <section class="recruiting-section">
                    <h4 class="recruiting-section__title">Patterns</h4>
                    <p class="recruiting-section__desc">
                      Pattern sets gate which opportunities qualify for
                      matching. Protopia defaults apply automatically;
                      customer extensions are additive.
                    </p>

                    <div class="recruiting-pattern-buttons">
                      <md-button
                        class="md-raised md-accent md-white-color capitalize"
                        @click="openPatternView('library')"
                      >
                        Pattern Library
                      </md-button>
                      <md-button
                        class="md-raised md-accent md-white-color capitalize"
                        @click="openPatternView('create')"
                      >
                        Create Pattern Set
                      </md-button>
                      <md-button
                        class="md-raised md-accent md-white-color capitalize"
                        @click="openPatternView('test')"
                      >
                        Test Pattern Set
                      </md-button>
                      <md-button
                        class="md-raised md-accent md-white-color capitalize"
                        @click="openPatternView('quarantine')"
                      >
                        Quarantine Review
                      </md-button>
                    </div>
                  </section>
                </template>

                <!-- Save -->
                <md-button
                  type="submit"
                  class="mt-20 md-raised md-round md-accent"
                >
                  {{ saveText }}
                </md-button>
              </form>
            </div>
          </md-card-content>
        </md-card-expand-content>
      </md-card-expand>
    </md-card>
  </div>
</template>

<script>
// Wired to core-api: PATCH /api/recruiting/:communityId on save, GET on mount.

import axios from 'axios';
import { apiToHermes, hermesToApi } from '../services/recruitingConfigAdapter';

const CORE_API_URL = process.env.CORE_API_URL || 'http://localhost:8090';
// Base URL of the recruiting wizard for the CURRENT env (display-only).
const RECRUITING_BASEURL = process.env.RECRUITING_BASEURL || 'https://opportunities.protopia.co';

export default {
  name: 'CommunityRecruitingConfig',

  props: {
    // Prop for parity with sibling sections; used to hydrate when backend lands.
    community: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  data() {
    return {
      saveText: 'Save',
      config: {
        moduleEnabled: false,
        jobPostingUrl: '',
        // "Learn More" URL used by the recruiting wizard footer. 
        learnMoreURL: '',

        administrators: {
          approvalNewJob: '',
          alertJob: '',
          alertFeedback: '',
          alertApplication: '',
          alertSecuredJob: '',
          alertUnsubscribe: '',
          alertProfileUpdate: '',
        },

        notifications: {
          requireApprovals: true,
          approvalEmails: '',
          jobReminder: false,
        },

        matching: {
          minAdvisorsPerJob: 25,
          refillEnabled: false,
          interestClicks: 5,
          daysToRefill: 3,
          minAdvisorsForRefill: 5,
          maxAdvisorsPerJob: 100,
        },
      },

      // Admin alert roles
      adminRoles: [
        {
          key: 'approvalNewJob',
          label: 'Approval – New Job',
          placeholder: 'Choose member…',
          helper: 'Notified when a manually submitted job requires review.',
        },
        {
          key: 'alertJob',
          label: 'Alert – Job',
          placeholder: 'Choose member…',
          helper: 'General notification of new job ingestions.',
        },
        {
          key: 'alertFeedback',
          label: 'Alert – Feedback',
          placeholder: 'Choose member…',
          helper: 'Notified when an alum provides feedback on an opportunity.',
        },
        {
          key: 'alertApplication',
          label: 'Alert – Application',
          placeholder: 'Choose member…',
          helper: 'Triggered when a member indicates they have applied.',
        },
        {
          key: 'alertSecuredJob',
          label: 'Alert – Secured Job',
          placeholder: 'Choose member…',
          helper: 'Notified when a member confirms they have secured a role.',
        },
        {
          key: 'alertUnsubscribe',
          label: 'Alert – Unsubscribe',
          placeholder: 'Choose member…',
          helper: 'Notified when a member opts out of Job Alerts.',
        },
        {
          key: 'alertProfileUpdate',
          label: 'Alert – Profile Update',
          placeholder: 'Choose member…',
          helper: 'Triggered when a member updates their data via job engagement.',
        },
      ],
    };
  },

  computed: {
    // Display-only URL for the readonly input. Environment-aware: local shows
    // localhost, dev shows recruiting-dev, prod shows the official domain.
    wizardURLDisplay() {
      if (!this.config.moduleEnabled) return '';
      if (!this.community || !this.community._id) return '';
      return `${RECRUITING_BASEURL}/submit/${this.community._id}`;
    },
  },

  watch: {
    // The community prop is loaded asynchronously by Community.vue and
    // already includes the `recruiting` sub-document. Hydrate straight from
    // the prop the moment it appears — no extra network call.
    'community.recruiting': {
      immediate: true,
      deep: true,
      handler(recruiting) {
        if (recruiting && Object.keys(recruiting).length > 0) {
          this.config = apiToHermes(recruiting);
        }
      },
    },
  },

  methods: {
    async save(scope) {
      const valid = await this.$validator.validateAll(scope);
      if (!valid) {
        this.$toast.error('Please fix the highlighted fields.');
        return;
      }
      if (!this.community || !this.community._id) {
        this.$toast.error('No community loaded — cannot save.');
        return;
      }
      try {
        const url = `${CORE_API_URL}/api/recruiting/${this.community._id}`;
        const payload = hermesToApi(this.config, this.community);
        const token = localStorage.getItem('auth0_token');
        const { data } = await axios.patch(
          url,
          payload,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        // Refresh local state with the server truth.
        this.config = apiToHermes(data);
        this.saveText = 'Saved';
        this.$toast.success('Recruiting configuration saved.');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[recruiting save]', err);
        this.$toast.error('Failed to save. Please try again.');
      }
    },

    openPatternView(kind) {
      // Pattern views are future tickets; toast keeps the buttons non-dead.
      const labels = {
        library: 'Pattern Library',
        create: 'Create Pattern Set',
        test: 'Test Pattern Set',
        quarantine: 'Quarantine Review',
      };
      this.$toast.success(
        `${labels[kind]} — route lands in a separate ticket (PRD §5.1.5 / §6.6).`,
      );
    },
  },
};
</script>

<style scoped>
/* Local layout helpers that keep each field / sub-section spaced out. */

/* sub-section (Module Activation, Administrators,
   Notifications, Matching Rules, Patterns). */
.recruiting-section {
  margin-top: 24px;
  padding: 16px 5px 12px;
  border-top: 1px solid #e5e7eb;
}
.recruiting-section:first-of-type {
  margin-top: 8px;
  border-top: none;
}
.recruiting-section__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #444;
}
.recruiting-section__desc {
  margin: 0 0 14px;
  font-size: 12px;
  color: #888;
}
.recruiting-field-row {
  margin: 0 0 20px;
}
.recruiting-field-row__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  margin-bottom: 4px;
}
.recruiting-field-row .md-field {
  margin: 0;
}
.recruiting-learn-more {
  margin-top: 28px;
  margin-bottom: 28px;
}
/* Every checkbox row across the panel: label sits flush-left with the
   checkbox and helper text hugs it (Max, 07/06). */
.recruiting-check-row {
  margin: 0 0 20px;
}
.recruiting-check-row .md-checkbox {
  margin: 0;
}
.recruiting-check-row .recruiting-helper {
  padding-left: 0;
  margin-top: 2px;
}
.recruiting-helper {
  margin: 4px 0 0;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}
.recruiting-error {
  margin: 4px 0 0;
  font-size: 12px;
  color: #ff0000;
}

.recruiting-pattern-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>

<!-- Non-scoped override: vue-material's global .md-checkbox-label rule wins
     over scoped styles here, so this block matches the project pattern
     (see main.css e.g. .disclaimer .md-checkbox .md-checkbox-label). -->
<style>
.recruiting-check-row .md-checkbox .md-checkbox-label {
  padding-left: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/CommunityRecruitingConfig.vue